import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Trash2, Package, RotateCcw, Loader2, Image,
  AlertTriangle, Eye
} from 'lucide-react';
import { fetchProducts, deleteProduct, restoreProduct } from '../../api/supplierApi';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import SecureImage from '../../components/supplier/SecureImage';
import ProductDetailPage from './ProductDetailPage';
import ProductCreatePage from './ProductCreatePage';
import ProductEditPage from './ProductEditPage';

// ── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{message}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl">İptal</Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">Sil</Button>
        </div>
      </div>
    </div>
  );
}

// ── Product Card ───────────────────────────────────────────────────────────────
function ProductCard({ p, tr, onView, onDelete, onRestore, deleting, restoring }) {
  const cover = p.image_objects?.find(i => i.is_cover) || p.image_objects?.[0];
  const imgCount = p.image_objects?.length || 0;
  const isDeleted = !!p.deleted_at;

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all group ${
        isDeleted ? 'border-red-100 opacity-75' : p.is_active ? 'border-gray-100 hover:shadow-md hover:border-amber-100' : 'border-gray-200 opacity-60'
      }`}
    >
      {/* Image */}
      <div
        onClick={() => !isDeleted && onView(p.id)}
        className={`aspect-video bg-gray-50 relative overflow-hidden ${!isDeleted ? 'cursor-pointer' : ''}`}
      >
        {cover?.thumb_url ? (
          <SecureImage
            src={cover.thumb_url}
            alt={p.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="h-10 w-10 text-gray-200" />
          </div>
        )}

        {/* Badges */}
        {isDeleted && (
          <span className="absolute top-2 left-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">{tr ? 'Silindi' : 'Deleted'}</span>
        )}
        {!isDeleted && !p.is_active && (
          <span className="absolute top-2 left-2 text-xs bg-gray-700 text-white px-2 py-0.5 rounded-full">{tr ? 'Pasif' : 'Inactive'}</span>
        )}
        {imgCount > 1 && (
          <span className="absolute bottom-2 right-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded-full">
            {imgCount} {tr ? 'görsel' : 'imgs'}
          </span>
        )}
        {/* View overlay */}
        {!isDeleted && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-xl px-3 py-1.5 text-xs font-semibold text-gray-700 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> {tr ? 'Detay' : 'View'}
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h4
          onClick={() => !isDeleted && onView(p.id)}
          className={`font-semibold text-gray-900 text-sm leading-tight line-clamp-2 mb-1.5 ${!isDeleted ? 'cursor-pointer hover:text-amber-600 transition-colors' : ''}`}
        >
          {p.title}
        </h4>

        {p.category && (
          <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{p.category}</span>
        )}

        <div className="flex items-center justify-between mt-3">
          <div>
            <span className="font-bold text-amber-600">
              {p.currency} {(() => {
                const pricing = p.pricing || {};
                let price = null;
                for (const k of ['event_duration', 'one_time', 'daily', 'weekly', 'monthly']) {
                  if (pricing[k] != null) { price = pricing[k]; break; }
                }
                if (price == null) price = p.price || 0;
                return parseFloat(price).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
              })()}
            </span>
            {p.unit && <span className="text-xs text-gray-400 ml-1">/ {p.unit}</span>}
          </div>

          {isDeleted ? (
            <Button size="sm" variant="ghost" onClick={() => onRestore(p)} disabled={restoring === p.id}
              className="h-8 px-3 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs font-medium">
              {restoring === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" />{tr ? 'Geri Al' : 'Restore'}</>}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onDelete(p)} disabled={deleting === p.id}
              className="h-8 w-8 p-0 text-red-400 hover:bg-red-50 rounded-lg">
              {deleting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          )}
        </div>

        {p.lead_time_days != null && (
          <p className="text-xs text-gray-400 mt-1.5">
            {p.lead_time_days === 0 || p.lead_time_days === '0'
              ? (tr ? 'Aynı gün teslimat' : 'Same day delivery')
              : (tr ? `Tedarik: ${p.lead_time_days} gün` : `Lead time: ${p.lead_time_days} days`)
            }
          </p>
        )}

        {/* Stok durumu */}
        {p.stock_policy && p.stock_policy !== 'untracked' && p.stock_quantity != null && (
          (() => {
            const total = p.stock_quantity || 0;
            const reserved = p.reserved_quantity || 0;
            const available = Math.max(0, total - reserved);
            const isCarton = p.calculation_type === 'carton_based';
            const unit = isCarton ? (tr ? 'koli' : 'Pack') : (tr ? 'adet' : 'pcs');
            const color = available === 0 ? 'bg-red-50 text-red-600' : available <= 5 ? 'bg-yellow-50 text-yellow-700' : 'bg-emerald-50 text-emerald-700';
            return (
              <div className={`mt-2 flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg ${color}`}>
                <span className="font-bold">{available} {unit}</span>
                <span className="opacity-60">{tr ? 'müsait' : 'available'}</span>
                {reserved > 0 && (
                  <span className="ml-auto opacity-50">{reserved} {tr ? 'rezerve' : 'reserved'}</span>
                )}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}

// ── Product List View ──────────────────────────────────────────────────────────
function ProductListView({ tr, onAdd, onView }) {
  const [products, setProducts] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [active, trash] = await Promise.all([
        fetchProducts(),
        fetchProducts({ deleted: true }).catch(() => []),
      ]);
      setProducts(active);
      setDeleted(trash);
    } catch (e) {
      toast.error(tr ? 'Ürünler yüklenemedi.' : 'Failed to load products.');
    } finally { setLoading(false); }
  }, [tr]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete.id);
    setConfirmDelete(null);
    try {
      await deleteProduct(confirmDelete.id);
      const removed = products.find(p => p.id === confirmDelete.id);
      setProducts(prev => prev.filter(p => p.id !== confirmDelete.id));
      if (removed) setDeleted(prev => [{ ...removed, deleted_at: new Date().toISOString() }, ...prev]);
      toast.success(tr ? 'Ürün çöp kutusuna taşındı.' : 'Product moved to trash.');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Silinemedi.' : 'Delete failed.'));
    } finally { setDeleting(null); }
  };

  const handleRestore = async (p) => {
    setRestoring(p.id);
    try {
      await restoreProduct(p.id);
      setDeleted(prev => prev.filter(d => d.id !== p.id));
      setProducts(prev => [{ ...p, deleted_at: null }, ...prev]);
      toast.success(tr ? 'Ürün geri alındı.' : 'Product restored.');
    } catch { toast.error(tr ? 'Geri alınamadı.' : 'Restore failed.'); }
    finally { setRestoring(null); }
  };

  // Sort by m² ascending (smallest → largest). Primary key is the explicit
  // `area_m2` field when present; otherwise fall back to parsing the first
  // "### m²" token from the title (covers the COP 31 modular stand catalog
  // where area is embedded in the name, e.g. "Tier 2 A - 50 m² …").
  // Products without any m² signal fall to the end — safe for generic
  // supplier lists where sorting is cosmetically a no-op.
  const areaKey = (p) => {
    if (Number.isFinite(+p?.area_m2) && +p.area_m2 > 0) return +p.area_m2;
    const t = `${p?.title || ''} ${p?.title_tr || ''} ${p?.title_en || ''}`;
    const m = t.match(/(\d{1,5})\s*m(?:²|2|\^2)/i);
    return m ? parseInt(m[1], 10) : Infinity;
  };
  const byAreaAsc = (arr) => [...arr].sort((a, b) => areaKey(a) - areaKey(b));
  const displayList = showDeleted ? byAreaAsc(deleted) : byAreaAsc(products);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Ürünlerim' : 'My Products'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {products.filter(p => p.is_active).length} {tr ? 'aktif ürün' : 'active'}
            {deleted.length > 0 ? ` · ${deleted.length} ${tr ? 'silinmiş' : 'deleted'}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {deleted.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowDeleted(s => !s)}
              className={`rounded-xl text-xs ${showDeleted ? 'border-red-300 text-red-600 bg-red-50' : 'border-gray-200 text-gray-500'}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {showDeleted ? (tr ? 'Aktif' : 'Active') : `${tr ? 'Çöp Kutusu' : 'Trash'} (${deleted.length})`}
            </Button>
          )}
          {!showDeleted && (
            <Button onClick={onAdd} data-testid="add-product-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
              <Plus className="h-4 w-4 mr-2" />{tr ? 'Ürün Ekle' : 'Add Product'}
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
      ) : displayList.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-56 text-center bg-white rounded-2xl border border-gray-100 border-dashed">
          <Package className="h-12 w-12 text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">
            {showDeleted ? (tr ? 'Çöp kutusunda ürün yok.' : 'No deleted products.') : (tr ? 'Henüz ürün eklenmedi.' : 'No products yet.')}
          </p>
          {!showDeleted && (
            <Button size="sm" onClick={onAdd} className="mt-4 bg-amber-500 text-white rounded-xl">
              <Plus className="h-4 w-4 mr-1.5" />{tr ? 'İlk ürünü ekle' : 'Add first product'}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayList.map(p => (
            <ProductCard
              key={p.id}
              p={p}
              tr={tr}
              onView={onView}
              onDelete={setConfirmDelete}
              onRestore={handleRestore}
              deleting={deleting}
              restoring={restoring}
            />
          ))}
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title={tr ? 'Ürünü Sil' : 'Delete Product'}
          message={tr
            ? `"${confirmDelete.title}" çöp kutusuna taşınacak. Daha sonra geri alabilirsiniz.`
            : `"${confirmDelete.title}" will be moved to trash.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Coordinator ────────────────────────────────────────────────────────────────
export default function ProductsSection({ tr, overrideBasePath, showLinkedPackage = false }) {
  const navigate   = useNavigate();
  const { slug, subsection, action } = useParams();

  // URL'den view türetiliyor
  // /products           → list
  // /products/new       → create
  // /products/:id       → detail
  // /products/:id/edit  → edit
  const view      = !subsection ? 'list'
                  : subsection === 'new' ? 'create'
                  : action === 'edit'    ? 'edit'
                  : 'detail';
  const productId = subsection && subsection !== 'new' ? subsection : null;

  const basePath = overrideBasePath || (slug
    ? `/tedarikci-admin/${slug}/dashboard/products`
    : '/tedarikci-admin/dashboard/products');

  const go = (newView, id = null) => {
    if (newView === 'create')      navigate(`${basePath}/new`);
    else if (newView === 'detail') navigate(`${basePath}/${id}`);
    else if (newView === 'edit')   navigate(`${basePath}/${id}/edit`);
    else                           navigate(basePath); // list
  };

  if (view === 'create') {
    return (
      <ProductCreatePage
        tr={tr}
        onBack={() => go('list')}
        onSaved={(id) => go('detail', id)}
      />
    );
  }

  if (view === 'detail') {
    return (
      <ProductDetailPage
        productId={productId}
        tr={tr}
        onBack={() => go('list')}
        onEdit={() => go('edit', productId)}
        onDelete={() => go('list')}
        onDuplicate={(copy) => go('detail', copy.id)}
        showLinkedPackage={showLinkedPackage}
      />
    );
  }

  if (view === 'edit') {
    return (
      <ProductEditPage
        productId={productId}
        tr={tr}
        onBack={() => go('detail', productId)}
        onSaved={(id) => go('detail', id)}
      />
    );
  }

  return (
    <ProductListView
      tr={tr}
      onAdd={() => go('create')}
      onView={(id) => go('detail', id)}
    />
  );
}

